import type { AnalyticsFiltersGroup } from '../../../../analyticsModule/components/AnalyticsFilterSelect/AnalyticsFiltersSelect.types'
import type {
  AnalyticsFilters,
  AnalyticsFiltersForVisibility,
  FilterGroupStateType,
} from '../../../../types'

/**
 * Get current filter values from URL search params
 * Validates values against available filter options and filters out invalid ones
 */
export const getCurrentFilterValues = (
  filterGroups: AnalyticsFilters | undefined,
  searchParams: URLSearchParams,
): Record<string, string[]> => {
  const currentValues: Record<string, string[]> = {}

  if (!filterGroups) return currentValues

  filterGroups.forEach((group) => {
    const paramName = group.key
    const paramValue = searchParams.get(paramName)

    // Get valid filter keys for this group
    const validFilterKeys = new Set(group.filters.map((filter) => filter.key))

    if (paramValue) {
      // Split comma-separated values for multi-select filters
      const urlValues = paramValue.split(',').filter(Boolean)
      // Filter out invalid values - only keep those that exist in validFilterKeys
      const validValues = urlValues.filter((value) => validFilterKeys.has(value))
      currentValues[paramName] = validValues
    } else if (group.selectionType === 'single') {
      // Use default values if no URL param for single selection groups
      currentValues[paramName] = group.filters
        .filter((filter) => filter.default_value)
        .map((filter) => filter.key)
    } else {
      // For multiple selection groups, start with empty array if no URL param
      currentValues[paramName] = []
    }
  })

  return currentValues
}

/**
 * Get filter state for UI components
 */
export const getFilterState = (
  filterGroups: AnalyticsFilters | undefined,
  currentValues: Record<string, string[]>,
): AnalyticsFiltersGroup[] => {
  if (!filterGroups) return []

  return filterGroups.map((group) => ({
    title: group.label,
    key: group.key,
    type: group.selectionType,
    items: group.filters.map((filter) => ({
      value: filter.key,
      label: filter.label,
      checked:
        group.selectionType === 'single'
          ? currentValues[group.key]?.[0] === filter.key
          : currentValues[group.key]?.includes(filter.key) || false,
    })),
  }))
}

/**
 * Get filters as object for API calls
 */
export const getFiltersForAPI = (
  currentValues: Record<string, string[]>,
): Record<string, string> => {
  const filters: Record<string, string> = {}

  Object.entries(currentValues).forEach(([groupKey, values]) => {
    if (values.length > 0) {
      filters[groupKey] = values.join(',')
    }
  })

  return filters
}

/**
 * Get default filter values from filterGroups
 */
const getDefaultFilterValues = (
  filterGroups: AnalyticsFilters | undefined,
): Record<string, string[]> => {
  const defaultValues: Record<string, string[]> = {}

  if (!filterGroups) return defaultValues

  filterGroups.forEach((group) => {
    const defaultFilterKeys = group.filters
      .filter((filter) => filter.default_value)
      .map((filter) => filter.key)

    defaultValues[group.key] = defaultFilterKeys
  })

  return defaultValues
}

export const getFiltersForVisibility = (
  filterGroups: AnalyticsFilters | undefined,
  currentValues: Record<string, string[]>,
  hasActiveFilters: boolean,
): AnalyticsFiltersForVisibility => {
  const filtersForVisibility: AnalyticsFiltersForVisibility = {}

  if (!filterGroups) return filtersForVisibility

  // If no active filters, use default values instead of current values
  const valuesToUse = hasActiveFilters ? currentValues : getDefaultFilterValues(filterGroups)

  filterGroups.forEach((group) => {
    const availableOptions = group.filters.map((filter) => filter.key)
    const selectedOptions = valuesToUse[group.key] || []
    const totalAvailable = availableOptions.length
    const totalSelected = selectedOptions.length

    let type: FilterGroupStateType

    if (totalSelected === 0) {
      type = 'none_selected'
    } else if (totalSelected === 1) {
      type = 'only_one_selected'
    } else if (totalSelected === totalAvailable) {
      type = 'all_selected'
    } else {
      type = 'multiple_selected'
    }

    filtersForVisibility[group.key] = {
      type,
      options: selectedOptions,
    }
  })

  return filtersForVisibility
}

/**
 * Clean up invalid filter params from URL search params
 * Removes invalid values and updates URL to only contain valid filter values
 */
export const cleanupInvalidFilterParams = (
  filterGroups: AnalyticsFilters | undefined,
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
): boolean => {
  if (!filterGroups || filterGroups.length === 0) {
    // If no filter groups, remove all filter params
    const newSearchParams = new URLSearchParams(searchParams)
    let hasChanges = false

    filterGroups?.forEach((group) => {
      if (newSearchParams.has(group.key)) {
        newSearchParams.delete(group.key)
        hasChanges = true
      }
    })

    if (hasChanges) {
      setSearchParams(newSearchParams)
    }

    return hasChanges
  }

  const newSearchParams = new URLSearchParams(searchParams)
  let hasChanges = false

  filterGroups.forEach((group) => {
    const paramName = group.key
    const paramValue = newSearchParams.get(paramName)

    if (!paramValue) {
      // No param value, nothing to clean up
      return
    }

    // Get valid filter keys for this group
    const validFilterKeys = new Set(group.filters.map((filter) => filter.key))

    // Split and filter out invalid values
    const urlValues = paramValue.split(',').filter(Boolean)
    const validValues = urlValues.filter((value) => validFilterKeys.has(value))

    if (validValues.length !== urlValues.length) {
      // Some values were invalid, update the param
      hasChanges = true
      if (validValues.length > 0) {
        newSearchParams.set(paramName, validValues.join(','))
      } else {
        // All values were invalid, remove the param
        newSearchParams.delete(paramName)
      }
    }
  })

  if (hasChanges) {
    setSearchParams(newSearchParams)
  }

  return hasChanges
}

/**
 * Update URL search params when filter changes
 */
export const updateFilterParam = (
  groupKey: string,
  filterKey: string,
  checked: boolean,
  groupType: 'single' | 'multiple',
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
) => {
  const newSearchParams = new URLSearchParams(searchParams)

  // Get current values from URL or use empty array
  const currentParamValue = newSearchParams.get(groupKey)
  const currentValues = currentParamValue ? currentParamValue.split(',').filter(Boolean) : []

  let newValues: string[]

  if (groupType === 'single') {
    // For single selection, only one value can be selected at a time
    newValues = checked ? [filterKey] : []
  } else if (checked) {
    // For multiple selection, add filter if not already present
    newValues = currentValues.includes(filterKey) ? currentValues : [...currentValues, filterKey]
  } else {
    // For multiple selection, remove filter
    newValues = currentValues.filter((value) => value !== filterKey)
  }

  if (newValues.length > 0) {
    newSearchParams.set(groupKey, newValues.join(','))
  } else {
    newSearchParams.delete(groupKey)
  }

  setSearchParams(newSearchParams)
}
